// Opencv algorithm based on leftCheek, rightCheek and Forehead Light values
export const checkLightQuality = (cv, video, face) => {
  const boxCanvas = document.createElement("canvas");
  const boxCanvasCtx = boxCanvas.getContext("2d");

  // Extract face box dimensions
  const { xMin, yMin, width, height } = face.box;
  boxCanvas.width = width;
  boxCanvas.height = height;
  // Calculate adjusted xMin to mirror canvas
  const adjustedXMin = video.videoWidth - (xMin + width);
  // Mirror the canvas before drawing, since AI model is generating results in mirror as well
  boxCanvasCtx.setTransform(-1, 0, 0, 1, width, 0);

  // Draw only the face region
  boxCanvasCtx.drawImage(
    video,
    adjustedXMin,
    yMin,
    width,
    height,
    0,
    0,
    width,
    height,
  );

  // Convert src image from RGBA to Y-color (Luma)
  let yChannel = convertRGBA2Luma(cv, boxCanvas);

  // Get light quality for leftCheek, rightCheek and forehead regions
  // Disabling Forehead, because for people with "Hair bangs" it covers foreahead and affect light estimator
  // const foreheadQuality = getLightQuality({
  //   cv: cv,
  //   lumaChannel: yChannel,
  //   faceBox: face.box,
  //   topLeftPoint: face.keypoints[67],
  //   bottomLeftPoint: face.keypoints[105],
  //   topRightPoint: face.keypoints[297],
  //   bottomRightPoint: face.keypoints[334],
  //   save: false,
  //   side: "forehead",
  // });

  const leftCheekQuality = getLightQuality({
    cv: cv,
    lumaChannel: yChannel,
    faceBox: face.box,
    topLeftPoint: face.keypoints[116],
    bottomLeftPoint: face.keypoints[147],
    topRightPoint: face.keypoints[120],
    bottomRightPoint: face.keypoints[203],
    save: false,
    side: "left",
  });

  const rightCheekQuality = getLightQuality({
    cv: cv,
    lumaChannel: yChannel,
    faceBox: face.box,
    topLeftPoint: face.keypoints[349],
    bottomLeftPoint: face.keypoints[423],
    topRightPoint: face.keypoints[340],
    bottomRightPoint: face.keypoints[376],
    save: false,
    side: "right",
  });

  const finalLightQuality = getFinalQuality(
    "good", //foreheadQuality
    leftCheekQuality,
    rightCheekQuality,
  );

  yChannel.delete();
  return finalLightQuality;
};

const convertRGBA2Luma = (cv, imageCanvas) => {
  const imageCanvasCtx = imageCanvas.getContext("2d");
  let src = new cv.Mat(imageCanvas.height, imageCanvas.width, cv.CV_8UC4);
  let interim = new cv.Mat(imageCanvas.height, imageCanvas.width, cv.CV_8UC3);
  let dst = new cv.Mat(imageCanvas.height, imageCanvas.width, cv.CV_8UC3);

  src.data.set(
    imageCanvasCtx.getImageData(0, 0, imageCanvas.width, imageCanvas.height)
      .data,
  );

  // No direct conversion available from RGBA to YCrCb
  cv.cvtColor(src, interim, cv.COLOR_RGBA2RGB);
  cv.cvtColor(interim, dst, cv.COLOR_RGB2YCrCb);
  let ycrcbPlanes = new cv.MatVector();
  // Split the Mat
  cv.split(dst, ycrcbPlanes);
  // Get Y channel
  let yChannel = ycrcbPlanes.get(0);

  src.delete();
  interim.delete();
  dst.delete();
  ycrcbPlanes.delete();

  return yChannel;
};

// Check light quality based on rect polygon and also adjust for mirror
const getLightQuality = ({
  cv,
  lumaChannel,
  faceBox,
  topLeftPoint,
  bottomLeftPoint,
  topRightPoint,
  bottomRightPoint,
  save,
  side,
} = {}) => {
  const mask = new cv.Mat.zeros(faceBox.height, faceBox.width, cv.CV_8UC1);

  // Adjust for crop box
  let pt1 = new cv.Point(
    Math.min(Math.max(topLeftPoint.x - faceBox.xMin, 0), faceBox.width),
    Math.min(Math.max(topLeftPoint.y - faceBox.yMin, 0), faceBox.height),
  );
  let pt2 = new cv.Point(
    Math.min(Math.max(bottomLeftPoint.x - faceBox.xMin, 0), faceBox.width),
    Math.min(Math.max(bottomLeftPoint.y - faceBox.yMin, 0), faceBox.height),
  );
  let pt3 = new cv.Point(
    Math.min(Math.max(topRightPoint.x - faceBox.xMin, 0), faceBox.width),
    Math.min(Math.max(topRightPoint.y - faceBox.yMin, 0), faceBox.height),
  );
  let pt4 = new cv.Point(
    Math.min(Math.max(bottomRightPoint.x - faceBox.xMin, 0), faceBox.width),
    Math.min(Math.max(bottomRightPoint.y - faceBox.yMin, 0), faceBox.height),
  );

  let pts = [pt1, pt3, pt4, pt2];
  let contours = new cv.MatVector();
  let poly = new cv.Mat(pts.length, 1, cv.CV_32SC2);

  for (let i = 0; i < pts.length; i++) {
    poly.intPtr(i, 0)[0] = pts[i].x;
    poly.intPtr(i, 0)[1] = pts[i].y;
  }

  contours.push_back(poly);
  // Fill the polygon in the mask
  cv.fillPoly(mask, contours, new cv.Scalar(255));

  // For debugging
  if (save) {
    let maskedLum = new cv.Mat();
    cv.bitwise_and(lumaChannel, lumaChannel, maskedLum, mask);
    let canvas = document.createElement("canvas");
    canvas.width = maskedLum.cols;
    canvas.height = maskedLum.rows;
    let ctx = canvas.getContext("2d");

    // Convert mask (Mat) to ImageData
    let imgData = new ImageData(maskedLum.cols, maskedLum.rows);
    let maskData = maskedLum.data;

    for (let i = 0; i < maskData.length; i++) {
      let value = maskData[i]; // Grayscale value
      imgData.data[i * 4] = value; // Red
      imgData.data[i * 4 + 1] = value; // Green
      imgData.data[i * 4 + 2] = value; // Blue
      imgData.data[i * 4 + 3] = 255; // Alpha (fully opaque)
    }

    ctx.putImageData(imgData, 0, 0);

    // Convert canvas to JPEG and trigger download
    let link = document.createElement("a");
    link.href = canvas.toDataURL("image/jpeg");
    link.download = `mask_${side}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    maskedLum.delete();
  }

  const maskMean = new cv.Mat();
  const maskStd = new cv.Mat();
  cv.meanStdDev(lumaChannel, maskMean, maskStd, mask);

  let lightQuality = "dark";
  if (
    maskMean.data64F[0] >= 60 &&
    maskMean.data64F[0] <= 200 &&
    maskStd.data64F[0] <= 200
  ) {
    lightQuality = "good";
  } else {
    // Check is it closer to bright limit or dark
    lightQuality = maskMean.data64F[0] - 170 > 0 ? "bright" : "dark"; // 170 is center point b/w 140 and 200
  }

  //   console.log("Mean", maskMean.data64F[0]);
  //   console.log("Std", maskStd.data64F[0]);

  // Clean-up
  mask.delete();
  contours.delete();
  maskMean.delete();
  maskStd.delete();

  return lightQuality;
};

// Check if all values are "good" otherwise return the most occuring value either "dark" or "bright"
const getFinalQuality = (value1, value2, value3) => {
  const values = [value1, value2, value3];

  if (values.every((value) => value === "good")) {
    return "good";
  }

  // Filter out only dark or bright
  const filteredValues = values.filter(
    (value) => value === "dark" || value === "bright",
  );

  const darkCounts = filteredValues.filter((value) => value === "dark");
  const brightCounts = filteredValues.filter((value) => value === "bright");

  // If total bright more than dark than return bright, default is dark since it occurs more often
  return brightCounts > darkCounts ? "bright" : "dark";
};
